import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, type ConfirmTone } from "@outpave/ds";

/** Translated copy for a drawer that words its warning its own way. */
export interface UnsavedChangesGuardOptions {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

/**
 * Every way out of a form runs `requestLeave`, which asks first while
 * `changed` holds; `confirmBefore` does the same for a way out that goes
 * somewhere else. Render `dialog` beside the drawer, never inside it.
 */
export function useUnsavedChangesGuard(
  changed: boolean,
  onLeave: () => void,
  options: UnsavedChangesGuardOptions = {},
) {
  const { t } = useTranslation();
  // The way out being asked about; none named means the drawer's own close, read when the person answers.
  const [pendingLeave, setPendingLeave] = useState<{
    leave?: () => void;
  } | null>(null);

  const confirmBefore = (leave: () => void) => {
    if (changed) {
      setPendingLeave({ leave });
      return;
    }
    leave();
  };
  const requestLeave = () => {
    if (changed) {
      setPendingLeave({});
      return;
    }
    onLeave();
  };

  const dialog = (
    <ConfirmDialog
      open={pendingLeave !== null}
      tone={options.tone ?? "primary"}
      title={options.title ?? t("Unsaved Changes")}
      message={
        options.message ??
        t("You have unsaved changes, are you sure you want to leave?")
      }
      confirmLabel={options.confirmLabel ?? t("Yes")}
      cancelLabel={options.cancelLabel ?? t("No")}
      onConfirm={() => {
        const leave = pendingLeave?.leave ?? onLeave;
        setPendingLeave(null);
        leave();
      }}
      onCancel={() => setPendingLeave(null)}
    />
  );

  return { requestLeave, confirmBefore, dialog };
}

export default useUnsavedChangesGuard;
